import { Logo } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createApprovalPoll, type PollSchedule } from '../lib/approval-poll';
import { probeApproval, SESSION_TOKEN, useSessionMe } from '../lib/session';
import { signOut } from '../lib/sign-out';
import { Button } from './ui/Button';

// The closed-beta waitlist wall. `<App/>` renders this INSTEAD of the app for a
// session `/v1/me` reports as `approved: false` — one the API has already
// capped to `read:account` (`sessionScopes`, apps/api/src/middleware/auth.ts),
// so the shell it used to land in was a whole console whose every panel 403'd
// on its own, with nothing on screen saying why.
//
// Reuses <ConsentGate/>'s `.login` card shell: no app chrome, and nothing here
// makes a call that could be refused. The only read is `/v1/me`, which is
// exactly what `read:account` still buys.
//
// It NAMES the identity that is waiting, because the commonest way to land here
// by accident is signing in with the wrong GitHub account and the wall looks
// identical either way. Sign-out is therefore the one control offered — that,
// and the invitation link, are the only two things a waitlisted person can do.
//
// It POLLS approval (plan 102 slice 05, `lib/approval-poll.ts`): bounded, visible-tab
// only, again on focus, stopped on approval and on unmount. On approval it moves the
// URL to the funnel FIRST and then re-reads the gate's `/v1/me`, so the app `<App/>`
// mounts in place of this wall opens on `/onboarding` (the bare path resolves to the
// person's account like any legacy path) — the waypoint `lib/invite-resume.ts` uses.

/** Where an approved person continues. */
const ONBOARDING_PATH = '/onboarding';

export function WaitlistGate(props: {
  token?: string | null;
  /** The poll's tick seam (`lib/approval-poll.ts`); tests drive it, the app never passes one. */
  pollSchedule?: PollSchedule;
}): JSX.Element {
  // Not a second read: `<App/>`'s gate already holds this exact query key (the
  // session probe, `useSessionMe`), so mounting
  // it here joins that cache entry rather than adding a request — which is only
  // true if we ask with the credential IT probed with, so an explicit `null`
  // (a deliberately signed-out caller) is honoured rather than replaced by the
  // ambient session. `token` is injectable for tests, like <AdminGate/>'s.
  const me = useSessionMe(props.token === undefined ? SESSION_TOKEN : props.token);
  // The human first, the account second — a user-bound session always carries
  // `user`, and `github_login` is the same person's handle for the one shape
  // that would not (a partial `/v1/me`). Never a blank line: an identity we
  // cannot name is the one fact this screen has to be able to state.
  const login = (): string | null => me.data?.user?.login ?? me.data?.github_login ?? null;
  // The address the approval mail actually goes to: `verdict.user.email`, and
  // no other (`apps/api/src/deps/mcp-ports.ts` sends `beta_access_approved`
  // only when the USER row carries one). The account's own `email` is a billing
  // address, so falling back to it would promise a mail that is never sent
  // there — the line renders only when we hold the address it names.
  const email = (): string | null => me.data?.user?.email ?? null;

  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  createApprovalPoll({
    check: () => probeApproval(token),
    onApproved: () => {
      window.history.replaceState(null, '', ONBOARDING_PATH);
      void me.refetch();
    },
    ...(props.pollSchedule === undefined ? {} : { schedule: props.pollSchedule }),
  });

  return (
    <main class="login">
      <div class="card login__card">
        <header class="card__header login__header">
          <span class="login__logo" aria-hidden="true">
            <Logo variant="icon" height={36} />
          </span>
          <h1 class="card__title login__title">{t('waitlist.title')}</h1>
          <p class="card__desc">{t('waitlist.body')}</p>
        </header>
        <div class="card__content login__content">
          <div class="alert" role="status">
            <Show when={login()}>
              {(name) => <p class="alert__title">{t('waitlist.waiting_as', { login: name() })}</p>}
            </Show>
            <Show when={email()}>
              {(address) => (
                <p class="alert__desc">{t('waitlist.waiting_email', { email: address() })}</p>
              )}
            </Show>
          </div>
          <div>
            <h2 class="card__title">{t('waitlist.next_title')}</h2>
            <p class="card__desc">{t('waitlist.next_body')}</p>
          </div>
          <div>
            <h2 class="card__title">{t('waitlist.invite_title')}</h2>
            <p class="card__desc">{t('waitlist.invite_body')}</p>
          </div>
          <div>
            <h2 class="card__title">{t('waitlist.wrong_account_title')}</h2>
            <p class="card__desc">{t('waitlist.wrong_account_body')}</p>
          </div>
          <Button variant="outline" onClick={() => void signOut()}>
            {t('session.sign_out')}
          </Button>
        </div>
      </div>
    </main>
  );
}
